import { jsx } from '@emotion/react';
import { Icon, Link, Tag } from '@zapier/design-system';
import { createUniqueClassName } from '../../utils/createUniqueClassName';
import { useUniversalNavContext } from '../../hooks';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH } from '../../../universal-nav/constants';
import { boxSizing, interactiveReset } from '../../../utils/style-mixins/reset';
const tooltipClassName = createUniqueClassName('UniversalNavLink', 'tooltip');
const tooltipSelector = `.${tooltipClassName}`;
const navIconClassName = createUniqueClassName('UniversalNavLink', 'navIcon');
const navIconSelector = `.${navIconClassName}`;
const externalIconClassName = createUniqueClassName('UniversalNavLink', 'externalIcon');
const externalIconSelector = `.${externalIconClassName}`;
const Styles = labelStyles('UniversalNavLink', {
    root: (props, hasBeforeText) => [
        withModifier('', Object.assign(Object.assign(Object.assign({}, interactiveReset), { font: `var(--zds-body-2)`, color: 'var(--zds-text-default, #413735)', display: 'flex', position: 'relative', minHeight: 'var(--zds-size-36, 36px)', padding: 'var(--zds-space-0, 0px) var(--zds-space-8, 8px)', borderRadius: 'var(--zds-radius-default)', whiteSpace: 'nowrap', width: '100%', transition: `0s`, textDecoration: 'none', '&:hover, &:focus': {
                transition: 'none',
                backgroundColor: 'var(--zds-background-stronger, #F5F3EB)',
                outline: 'none',
            }, [navIconSelector]: {
                color: 'var(--zds-text-default, #413735)',
            }, [`@container (max-width: ${UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH}px)`]: {
                width: 'var(--zds-space-36, 36px)',
            }, [`&:hover ${navIconSelector}, &:focus ${navIconSelector}`]: {
                transition: 'none',
                color: 'var(--zds-text-default, #413735)',
            } }), (props.isSidebar && {
            borderRadius: 'var(--zds-radius-small)',
            color: 'var(--zds-text-default, #413735)',
            font: `var(--zds-body-1)`,
            outline: 'solid 2px transparent',
            '&::before': {
                content: '""',
                position: 'absolute',
                pointerEvents: 'none',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                border: '2px solid transparent',
                borderRadius: 'var(--zds-radius-small)',
            },
            '&:focus': {
                backgroundColor: 'var(--zds-background-weaker, #FFFDF9)',
            },
            '&:hover': {
                transition: 'none',
                backgroundColor: 'var(--zds-background-stronger, #F5F3EB)',
                outline: `solid 2px transparent`,
            },
            '&:focus-visible': {
                '&::before': {
                    border: 'var(--zds-border-focus, 2px solid #695BE8)',
                    top: '-2px',
                    right: '-2px',
                    bottom: '-2px',
                    left: '-2px',
                },
            },
            [navIconSelector]: {
                color: 'var(--zds-text-default, #413735)',
            },
            [`&:hover ${navIconSelector}, &:focus ${navIconSelector}`]: {
                transition: 'none',
            },
        }))),
        !hasBeforeText &&
            withModifier('textOnly', {
                paddingLeft: 15,
                paddingRight: 15,
            }),
        props.isSelected &&
            withModifier('selected', Object.assign(Object.assign({ color: 'var(--zds-ui-primary-strongest, #503EBD)', backgroundColor: 'var(--zds-ui-primary-weakest, #F7F6FD)', [navIconSelector]: {
                    color: 'var(--zds-ui-primary-strongest, #503EBD)',
                } }, (props.isSidebar && {
                font: `var(--zds-body-3)`,
                color: 'var(--zds-text-default, #413735)',
                backgroundColor: 'var(--zds-ui-brand-weakest, #FDF5F2)',
                [navIconSelector]: { color: 'var(--zds-ui-brand, #FF4F00)' },
            })), { '&:hover, &:focus, &:focus-visible': {
                    backgroundColor: props.isSidebar
                        ? 'var(--zds-ui-brand-weakest, #FDF5F2)'
                        : 'var(--zds-ui-primary-weakest, #F7F6FD)',
                } })),
        {
            [tooltipSelector]: {
                opacity: 0,
                pointerEvents: 'none',
                transition: 'opacity 0.1s ease-in-out',
                '@media (hover: none)': {
                    display: 'none',
                },
            },
            [`&:hover ${tooltipSelector}, &:focus ${tooltipSelector}`]: {
                opacity: 1,
                pointerEvents: 'auto',
            },
        },
        {
            [externalIconSelector]: {
                opacity: 0,
            },
            [`&:hover ${externalIconSelector}, &:focus ${externalIconSelector}`]: {
                opacity: 0.5,
            },
        },
    ],
    layout: Object.assign(Object.assign({}, boxSizing), { display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto', gap: 20, alignItems: 'center', overflow: 'hidden', width: '100%' }),
    v3Layout: Object.assign(Object.assign({}, boxSizing), { display: 'flex', gap: 'var(--zds-space-12, 12px)', alignItems: 'center', overflow: 'hidden', width: '100%' }),
    text: (showCheckmark) => [
        Object.assign(Object.assign({}, boxSizing), { display: 'flex', flexDirection: 'column', minWidth: 0, '&[aria-hidden="true"]': {
                opacity: 0,
            } }),
        showCheckmark &&
            withModifier('showCheckmark', {
                flexGrow: 1,
            }),
    ],
    primaryText: Object.assign(Object.assign({}, boxSizing), { textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }),
    secondaryText: (isSelected) => [
        Object.assign(Object.assign({}, boxSizing), { font: `var(--zds-small-print-1)`, color: 'var(--zds-gray-warm-9, #413735)' }),
        isSelected &&
            withModifier('selected', {
                color: 'var(--zds-ui-primary-strongest, #503EBD)',
            }),
    ],
});
const UniversalNavLink = ({ href, text, icon = undefined, isExternal = false, isSelected = false, isSidebar = undefined, isV3Link = false, onClick: onClickProp = undefined, role = undefined, secondaryText = undefined, showCheckmarkOnSelected = false, tag = undefined, target = undefined, renderBeforeText = (props) => props.icon && (jsx("span", { className: navIconClassName },
    jsx(Icon, { isBlock: true, name: props.icon, size: isSidebar ? 20 : 18 }))), }) => {
    const { getFullyQualifiedURL } = useUniversalNavContext();
    const props = {
        href,
        text,
        icon,
        isExternal,
        isSelected,
        isSidebar,
        onClick: onClickProp,
        role,
        target,
        renderBeforeText,
    };
    const onClick = (e) => {
        e.currentTarget.blur();
        if (onClickProp) {
            onClickProp(e);
        }
    };
    const beforeText = renderBeforeText && renderBeforeText(props);
    return (jsx(Link, { color: null, css: Styles.root({ isSidebar, isSelected }, !!beforeText), href: getFullyQualifiedURL(href), onClick: onClick, role: role, target: target || '_self' },
        jsx("span", { css: isV3Link ? Styles.v3Layout : Styles.layout, "data-zds": true },
            beforeText,
            jsx("span", { css: Styles.text(showCheckmarkOnSelected), "data-zds": true },
                jsx("span", { css: Styles.primaryText, "data-zds": true }, text),
                secondaryText && (jsx("span", { css: Styles.secondaryText(isSelected), "data-zds": true }, secondaryText))),
            tag && (jsx(Tag, { size: "small", variant: "simple" }, tag)),
            isSelected && !isSidebar && showCheckmarkOnSelected && (jsx("span", { "data-zds": true },
                jsx(Icon, { isBlock: true, name: "formCheck", size: 24 }))),
            isExternal && (jsx("span", { className: externalIconClassName, "data-zds": true },
                jsx(Icon, { isBlock: true, name: "arrowOffsite", size: isSidebar ? 20 : 18 }))))));
};
export default UniversalNavLink;
//# sourceMappingURL=UniversalNavLink.js.map
// @zapier/universal-layout@12.1.15 (040754877)
